import { useQuery } from '@tanstack/react-query';
import type { CareCommunicationAckType } from '@/hooks/useCareCommunications';
import { env } from '@/lib/env';
import { supabase } from '@/lib/supabase';
import { operationOutcomeText } from '@/pages/medcom/acknowledgementDisplay';

type AcknowledgementOutcomeSource = {
  id: string;
  ackType: CareCommunicationAckType;
  errorMessage: string | null;
};

async function fetchAcknowledgementPayload(
  clinicId: string,
  messageId: string,
  acknowledgementId: string,
): Promise<unknown> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  const qs = new URLSearchParams({
    clinicId,
    messageId,
    format: 'json',
    acknowledgementId,
  });
  const resp = await fetch(
    `${env.VITE_SUPABASE_URL}/functions/v1/communicate-export?${qs.toString()}`,
    {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${session?.access_token ?? ''}`,
        apikey: env.VITE_SUPABASE_ANON_KEY,
      },
    },
  );
  if (!resp.ok) return null;
  return await resp.text();
}

/**
 * Negative receipts often arrive with an empty error column. The text is in
 * the stored bundle, so read that when the list did not include it.
 */
export function useAcknowledgementOutcomeTexts(
  clinicId: string | null | undefined,
  messageId: string | null | undefined,
  acknowledgements: AcknowledgementOutcomeSource[] | undefined,
) {
  const missingIds = (acknowledgements ?? [])
    .filter((ack) => ack.errorMessage === null && (ack.ackType === 'AE' || ack.ackType === 'AR'))
    .map((ack) => ack.id)
    .sort();
  const missingKey = missingIds.join(',');

  return useQuery({
    queryKey: ['care-communications', 'ack-outcome', clinicId, messageId, missingKey],
    enabled: Boolean(clinicId && messageId && missingIds.length > 0),
    staleTime: 60_000,
    queryFn: async (): Promise<Record<string, string | null>> => {
      if (!clinicId || !messageId) return {};
      const entries = await Promise.all(
        missingIds.map(async (acknowledgementId) => {
          try {
            const payload = await fetchAcknowledgementPayload(
              clinicId,
              messageId,
              acknowledgementId,
            );
            return [acknowledgementId, operationOutcomeText(payload)] as const;
          } catch {
            return [acknowledgementId, null] as const;
          }
        }),
      );
      return Object.fromEntries(entries);
    },
  });
}
